import React, { useEffect, useLayoutEffect } from 'react'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/divider.css'
import styles from './styles.module.scss'
import { ColourVariation } from 'lib/colourVariation'
import { Hotel as HotelData } from 'data/rating.json'
import clsx from 'clsx'
import { Hotel } from 'components/hotel'
import { useInView } from 'react-intersection-observer'

interface Props {
  categoryTitle: string
  colors: ColourVariation
  shortName: string
  id: string
  hotelList: HotelData[]
  currentCategorySetter: (id: string) => void
}

export function HotelCategory({
  categoryTitle,
  colors,
  hotelList,
  id,
  currentCategorySetter
}: Props) {
  const [ref, inView] = useInView({ rootMargin: '-25%' })

  useEffect(() => {
    if (inView) {
      currentCategorySetter(id)
    }
  }, [inView])

  const segmentClasses = clsx(
    `ui relaxed vertical attached segment`,
    styles.hotelCategory,
    id
  )

  const hotelMapToProps = (hotel: HotelData) => ({
    address: hotel.Adresse,
    category: hotel.Kategorie,
    description: hotel.Legende,
    host: hotel.Gastgeber,
    image: hotel.Bild,
    winnerText: hotel.Siegertext,
    name: hotel.Name,
    phone: hotel.Telefon,
    previousRank: hotel['Rang im Vorjahr'],
    price: hotel.Preis,
    rank: parseInt(hotel.Rang, 10),
    stars: parseInt(hotel.Sterne, 10),
    typeOfStay: hotel.Uebernachtungstyp,
    website: hotel.Webseite
  })

  // sort the hotel list and retrieve the first place
  hotelList.sort((a, b) => a.Rang - b.Rang)
  const firstPlace = hotelList[0]
  let firstHotelProps = {}
  if (firstPlace) {
    firstHotelProps = hotelMapToProps(firstPlace)
  }
  const remainingHotels = hotelList.slice(1)

  // Make sure that numbers are not separated from the words that follow
  // through a line break
  const cleanedCategoryTitle = categoryTitle.replace(
    /\d+\W+\w+/g,
    '<span class="nobreak">$&</span>'
  )

  return (
    <main className={segmentClasses} id={id} ref={ref}>
      <div className='ui text container'>
        <h2
          className={styles.categoryHeader}
          style={{ color: colors.gradientEnd }}
          dangerouslySetInnerHTML={{ __html: cleanedCategoryTitle }}
        />
        <div className='ui horizontal divider'>
          <i className='grey disabled snowflake icon' />
        </div>
      </div>

      <div className='ui container'>
        <div className={clsx(id, styles.winnerSegment)}>
          {firstPlace && (
            <Hotel
              className={clsx(styles.winnerCard, 'fluid')}
              {...firstHotelProps}
              colors={colors}
            />
          )}
          <div className={clsx(styles.winnerText, 'mobile hidden')}>
            <h3 className={clsx(id)}>
              <div className={clsx(styles.subHeader, 'sub header')}>
                {firstPlace && firstPlace.Name}
              </div>
              {firstPlace && firstPlace.SiegertextTitel}
            </h3>

            <p>{firstPlace && firstPlace.Siegertext}</p>
          </div>
        </div>

        <div className='ui vertical fitted segment'>
          <div className='ui three doubling stackable cards'>
            {remainingHotels.map(hotel => {
              const hotelProps = hotelMapToProps(hotel)
              return (
                <Hotel
                  {...hotelProps}
                  colors={colors}
                  key={hotelProps.name + '-' + hotelProps.rank}
                />
              )
            })}
          </div>
        </div>
      </div>
    </main>
  )
}
